import { ContentNotAvailable } from 'components/ContentNotAvailable'
import HealthChart from 'components/HealthChart'
import { MainBox } from 'components/MainBox'
import { labelStyles } from 'components/MainComponent/LabelStyles'
import { nfp, Textbuilder } from 'components/MainComponent/textBuilder'
import { TamediaLink } from 'components/TamediaLink'
import { ReadDataFromWindow } from 'components/WindowCommunication'
import { useLabels } from 'Hooks/useLabels'
import { useState } from 'react'

export const Part5 = ({
  tenant,
  colorMode
}: {
  tenant: Tenant | undefined
  colorMode: 'light' | 'dark' | undefined
}) => {
  const [voteStats, setvoteStats] = useState<VoteStatsType>({})
  const [userinputs, setUserInputs] = useState<UserInputsType>({})

  const Labels = useLabels(labelStyles)
  function handleNewData (data: dbDataType) {
    if (data && data.userinputs && data.userinputs.weight)
      setUserInputs(data.userinputs)

    if (data && data.voteStats && data.voteStats.sum)
      setvoteStats(data.voteStats)
  }

  let userSingleDose = userinputs.weight && userinputs.weight * 3
  const lang = tenant?.lang

  return (
    <>
      <ReadDataFromWindow callback={handleNewData} />
      {!userinputs.weight && (
        <>
          <MainBox
            content={
              <div
                style={{
                  color: 'var(--text-color)',
                  letterSpacing: '.5px'
                }}
              >
                <Labels userRelationBox>
                  {lang === 'de'
                    ? 'So gesund ist Kaffee-Konsum'
                    : 'Découvrez si votre consommation de café est saine'}
                </Labels>
              </div>
            }
          />
          <ContentNotAvailable>
            {lang === 'de' ? (
              <>
                Trinken Sie mehr, als für die Gesundheit unbedenklich ist?
                Finden Sie es heraus, indem Sie ganz oben Ihren Kaffee-Konsum
                angeben.
              </>
            ) : (
              <>
                Buvez-vous plus que ce qui est recommandé?
                Découvrez-le en indiquant tout en haut votre consommation de
                café.
              </>
            )}
          </ContentNotAvailable>
        </>
      )}
      {userinputs.weight && (
        <>
          <MainBox
            content={
              <div
                style={{
                  color: 'var(--text-color)',
                  letterSpacing: '.5px'
                }}
              >
                <>
                  {!!(
                    (userinputs.caffeine || userinputs.caffeine === 0) &&
                    userinputs.caffeine < userinputs.weight * 5.7
                  ) && (
                    <Labels userRelationBox>
                      {lang === 'de'
                        ? (userinputs.caffeine === 0 ? 'Moderater ' : 'Ihr ') +
                          'Kaffeekonsum ist für die Gesundheit unbedenklich, vielleicht sogar förderlich'
                        : 'Votre consommation de café ne présente aucun risque pour la santé.'}{' '}
                    </Labels>
                  )}
                  {!!(
                    userinputs.caffeine &&
                    userinputs.caffeine >= userinputs.weight * 5.7
                  ) && (
                    <Labels userRelationBox>
                      {lang === 'de'
                        ? 'Ihr Kaffeekonsum ist risikoreich'
                        : 'Votre consommation de café est supérieure à la limite recommandée.'}
                    </Labels>
                  )}
                </>
              </div>
            }
          />
          <p>
            {!!(
              userinputs.caffeine &&
              voteStats.caffeineAverage &&
              userinputs.health
            ) &&
              (lang === 'de'
                ? Textbuilder()
                    .add('Gemäss Ihren Angaben nehmen Sie')
                    .add(' täglich etwa ')
                    .num(userinputs.caffeine)
                    .add(' Milligramm Koffein')
                    .add(' zu sich. Das ist')
                    .compare(userinputs.caffeine, voteStats.caffeineAverage, {
                      '>': ' mehr als',
                      '<': ' weniger als',
                      '=': ' genau so viel wie'
                    })
                    .add(' der Durchschnitt aller Teilnehmenden')
                    .conditional(
                      (userinputs.caffeine < voteStats.caffeineAverage &&
                        userinputs.caffeine < userinputs.health) ||
                        (userinputs.caffeine > voteStats.caffeineAverage &&
                          userinputs.caffeine > userinputs.health) ||
                        (userinputs.caffeine === voteStats.caffeineAverage &&
                          userinputs.caffeine === userinputs.health),
                      { true: ' und', false: ', aber' }
                    )
                    .compare(userinputs.caffeine, userinputs.health, {
                      '>': ' liegt über dem Grenzwert.',
                      '<': ' liegt im Bereich, der für gesunde Erwachsene als unbedenklich gilt.',
                      '=': ' genau so viel wie die Menge, welche für gesunde Erwachsene als unbedenklich gilt.'
                    })

                    .break()
                    .add(' Laut der ')
                    .add('Europäischen Behörde für Lebensmittelsicherheit')
                    .break()
                    .add(' liegt die')
                    .add(' Grenze bei 5,7 Milligramm')
                    .add(' pro Kilogramm Körpergewicht.')
                    .break()
                    .add(' Sie sollten also ')
                    .num(userinputs.health, { digits: 1, style: 'percent' })
                    .add(' Milligramm täglich ')
                    .add(' nicht überschreiten.')
                    .array()
                    .map((m, i) => {
                      if ([3, 9].includes(i)) {
                        return (
                          <Labels userRelation key={i}>
                            {m}
                          </Labels>
                        )
                      }
                      if ([1].includes(i)) {
                        return (
                          <>
                            <TamediaLink href='https://www.efsa.europa.eu/de/topics/topic/caffeine#:~:text=4.,Koffein%20kann%20unbedenklich%20konsumiert%20werden&text=Einzeldosen%20von%20bis%20zu%20200,die%20gesunde%20erwachsene%20Allgemeinbev%C3%B6lkerung%20unbedenklich'>
                              {m}
                            </TamediaLink>{' '}
                          </>
                        )
                      }

                      return <span key={i}>{m}</span>
                    })
                : Textbuilder()
                    .add('Selon vos réponses, vous consommez')
                    .add(' environ ')
                    .num(userinputs.caffeine)
                    .add(' milligrammes de caféine')
                    .add(" par jour. C'est")
                    .compare(userinputs.caffeine, voteStats.caffeineAverage, {
                      '>': ' plus que',
                      '<': ' moins que',
                      '=': ' exactement le même que'
                    })
                    .add(' la moyenne des participants')
                    .conditional(
                      (userinputs.caffeine < voteStats.caffeineAverage &&
                        userinputs.caffeine < userinputs.health) ||
                        (userinputs.caffeine > voteStats.caffeineAverage &&
                          userinputs.caffeine > userinputs.health) ||
                        (userinputs.caffeine === voteStats.caffeineAverage &&
                          userinputs.caffeine === userinputs.health),
                      { true: ' et', false: ', mais' }
                    )
                    .compare(userinputs.caffeine, userinputs.health, {
                      '>': ' supérieur à la limite.',
                      '<': ' dans la zone considérée comme sans risque pour les adultes.',
                      '=': ' exactement la même que la quantité considérée comme saine pour les adultes.'
                    })

                    .break()
                    .add(' Selon l’')
                    .add('Agence européenne de sécurité des aliments')
                    .break()
                    .add(' la')
                    .add(' limite est de 5,7 milligrammes')
                    .add(' par kilogramme de poids corporel.')
                    .break()
                    .add(' Vous ne devriez donc pas dépasser ')
                    .num(userinputs.health, { digits: 1, style: 'percent' })
                    .add(' milligrammes par jour. ')
                    .array()
                    .map((m, i) => {
                      if ([3, 9].includes(i)) {
                        return (
                          <Labels userRelation key={i}>
                            {m}
                          </Labels>
                        )
                      }
                      if ([1].includes(i)) {
                        return (
                          <>
                            <TamediaLink href='https://www.efsa.europa.eu/de/topics/topic/caffeine#:~:text=4.,Koffein%20kann%20unbedenklich%20konsumiert%20werden&text=Einzeldosen%20von%20bis%20zu%20200,die%20gesunde%20erwachsene%20Allgemeinbev%C3%B6lkerung%20unbedenklich'>
                              {m}
                            </TamediaLink>{' '}
                          </>
                        )
                      }

                      return <span key={i}>{m}</span>
                    }))}
            {!!(
              !userinputs.caffeine &&
              voteStats.caffeineAverage &&
              voteStats.potCountAverage &&
              userinputs.health
            ) &&
              (lang === 'de'
                ? Textbuilder()
                    .add('Gemäss Ihren Angaben trinken Sie keinen Kaffee.')
                    .break()
                    .add(' Der Durchschnitt der Umfrage liegt bei ')
                    .break()
                    .num(voteStats.potCountAverage, { digits: 0 })
                    .plural(voteStats.potCountAverage, {
                      '0': ' Kaffee',
                      '1': ' Kaffees',
                      '2': ' Kaffees'
                    })
                    .add('und ')
                    .break()
                    .num(voteStats.caffeineAverage, { digits: 0 })
                    .add(' Milligramm Koffein pro Tag. Das ist')
                    .break()
                    .compare(voteStats.caffeineAverage, 400, {
                      '>': ' mehr als die',
                      '<': ' weniger als die',
                      '=': ' gleichviel wie die'
                    })
                    .add(
                      ' die Menge, welche für gesunde Erwachsene als unbedenklich gilt. Laut der '
                    )
                    .break()
                    .add('Europäischen Behörde für Lebensmittelsicherheit')
                    .break()
                    .add(
                      ' sind täglich 400 Milligramm (etwa 5,7 Milligramm pro Kilogramm Körpergewicht) kein Problem für gesunde Erwachsene.'
                    )
                    .array()
                    .map((m, i) => {
                      if ([0, 9].includes(i)) {
                        return (
                          <Labels userRelation key={i}>
                            {m}
                          </Labels>
                        )
                      }
                      if ([5].includes(i)) {
                        return (
                          <TamediaLink href='https://www.efsa.europa.eu/de/topics/topic/caffeine#:~:text=4.,Koffein%20kann%20unbedenklich%20konsumiert%20werden&text=Einzeldosen%20von%20bis%20zu%20200,die%20gesunde%20erwachsene%20Allgemeinbev%C3%B6lkerung%20unbedenklich'>
                            {m}
                          </TamediaLink>
                        )
                      }

                      return <span key={i}>{m}</span>
                    })
                : Textbuilder()
                    .add('Selon vos réponses, vous ne buvez pas de café.')
                    .break()
                    .add(' La moyenne des participants est de ')
                    .break()
                    .num(voteStats.potCountAverage, { digits: 0 })
                    .plural(voteStats.potCountAverage, {
                      '0': ' tasse de café',
                      '1': ' tasses de café',
                      '2': ' tasses de café'
                    })
                    .add('et ')
                    .break()
                    .num(voteStats.caffeineAverage, { digits: 0 })
                    .add(" milligrammes de caféine par jour. C'est")
                    .break()
                    .compare(voteStats.caffeineAverage, 400, {
                      '>': ' plus que la limite',
                      '<': ' moins que la limite',
                      '=': ' exactement la même que la quantité considérée comme saine pour les adultes.'
                    })
                    .add(
                      " la quantité considérée comme sans risque pour les adultes en bonne santé. Selon l'"
                    )
                    .break()
                    .add('Agence européenne de sécurité des aliments')
                    .break()
                    .add(
                      ' 400 milligrammes (soit 5,7 milligrammes par kilogramme de poids corporel) de caféine par jour ne posent aucun problème pour les adultes en bonne santé.'
                    )
                    .array()
                    .map((m, i) => {
                      if ([0, 9].includes(i)) {
                        return (
                          <Labels userRelation key={i}>
                            {m}
                          </Labels>
                        )
                      }
                      if ([5].includes(i)) {
                        return (
                          <TamediaLink href='https://www.efsa.europa.eu/de/topics/topic/caffeine#:~:text=4.,Koffein%20kann%20unbedenklich%20konsumiert%20werden&text=Einzeldosen%20von%20bis%20zu%20200,die%20gesunde%20erwachsene%20Allgemeinbev%C3%B6lkerung%20unbedenklich'>
                            {m}
                          </TamediaLink>
                        )
                      }

                      return <span key={i}>{m}</span>
                    }))}
          </p>
          <HealthChart
            lang={tenant?.lang}
            data={[
              [0, userinputs.caffeine || 0],
              [1, voteStats.caffeineAverage || 0],
              [2, userinputs.health || 0]
            ]}
            svgHeight={180}
          />
        </>
      )}{' '}
      <p>
        {userinputs.health && userSingleDose && (
          <Labels userRelation>
            {lang === 'de' ? (
              <>
                Pro «Einzeldosis» liegt Ihre Grenze bei {nfp(userSingleDose)}{' '}
                Milligramm Koffein (etwa 3 Milligramm pro Kilogramm
                Körpergewicht).
              </>
            ) : (
              <>
                Par «dose unique», votre limite est de {nfp(userSingleDose)}{' '}
                milligrammes de caféine (environ 3 milligrammes par kilogramme
                de poids corporel).
              </>
            )}
          </Labels>
        )}{' '}
        {lang === 'de' ? (
          <>
            Laut der Europäischen Behörde für Lebensmittelsicherheit können sich
            schon Dosen von 100 Milligramm bei einigen Erwachsenen auf
            Schlafdauer und -muster auswirken, insbesondere, wenn sie kurz vor
            dem Schlafengehen eingenommen werden. Gemäss dem{' '}
            <TamediaLink href='https://www.blv.admin.ch/blv/de/home/lebensmittel-und-ernaehrung/ernaehrung/empfehlungen-informationen/naehrstoffe/lebensmittel.html'>
              Bundesamt für Gesundheit
            </TamediaLink>{' '}
            können sehr hohe Dosen koffeinhaltiger Getränke schädlich sein,
            kurzfristig Schlafstörungen bewirken und Angstgefühle verstärken
            sowie langfristig zu Herz-Kreislauf-Problemen führen.
          </>
        ) : (
          <>
            Selon l'Autorité européenne de sécurité des aliments, des doses de
            100 milligrammes peuvent déjà avoir un impact sur la durée et le
            schéma du sommeil chez certains adultes, en particulier si elles
            sont absorbées juste avant le coucher. Selon{' '}
            <TamediaLink href='https://www.blv.admin.ch/blv/fr/home/lebensmittel-und-ernaehrung/ernaehrung/empfehlungen-informationen/naehrstoffe/lebensmittel.html'>
              l'Office fédéral de la santé publique
            </TamediaLink>{' '}
            , des doses très élevées de boissons contenant de la caféine peuvent
            être nocives, provoquer des troubles du sommeil et renforcer
            l'anxiété à court terme et entraîner des problèmes cardiovasculaires
            à long terme.
          </>
        )}
      </p>
    </>
  )
}
